import i18next from 'i18next';
import {List, Record} from 'immutable';
import moment from 'moment-timezone';
import {type Moment} from 'moment-timezone';

import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import generateUUID from 'web-app/util/uuid';
import {type EntityModel} from 'web-app/react/entities/factory';
import {
    FinancePeriodFunding,
    type PricedPlanPart as GqlPlanPart,
    FinancePeriod,
    type PlanPriceModifier,
    type PricedPlan,
    type Maybe,
    type PlanPublicFundingSettings,
} from 'web-app/util/graphql/api-types';
import type {CreateUpdatePlanPayload, PlanPartApi} from 'web-app/react/plans/manage-plan/types/manage-plan-types';

import type * as Constants from './constants';
import PlanStateEntity, {type PlanState} from '../common/plan-state';
import ProductBookingEntity, {type ProductBooking} from '../common/product-booking';
import SessionBookingEntity, {type SessionBooking} from '../common/session-booking';

interface IDiscount {
    readonly amount: number;
    readonly isPercent: boolean;
    readonly period: Constants.FREQUENCY;
    readonly title: string;
    readonly ordering: number;
    readonly showOnInvoice: boolean;
    readonly origin: Constants.DISCOUNT_ORIGIN;
    readonly discountId: string;
    readonly discountPresetId?: string;

    // isSurchage is a front end only flag that is used to split up discounts
    // and still be able to show the user a warning that negative discounts/surcharges is not allowed
    // for UX purposes
    readonly isSurcharge: boolean;
}

export class Discount extends Record<IDiscount>({
    amount: 0,
    isPercent: true,
    period: 'WEEKLY',
    title: '',
    ordering: 0,
    showOnInvoice: true,
    isSurcharge: true,
    origin: 'custom',
    discountId: '',
    discountPresetId: undefined,
}) {}

const discountFromAPI = (data: any) => {
    return new Discount({
        isSurcharge: data.amount < 0,
        discountId: generateUUID(),
        ...data,
        title: data.title || i18next.t('discounts.discount'),
    });
};

const discountPeriodToFrequency = (period: FinancePeriod): Constants.FREQUENCY => {
    switch (period) {
        case FinancePeriod.DAILY:
            return 'DAILY';
        case FinancePeriod.WEEKLY:
            return 'WEEKLY';
        case FinancePeriod.MONTHLY:
            return 'MONTHLY';
        case FinancePeriod.YEARLY:
            return 'YEARLY';
        default:
            return 'DAILY';
    }
};

const discountFromGraphQL = (data: PlanPriceModifier) => {
    if (data.__typename === 'PlanDiscount') {
        return new Discount({
            amount: data.amount,
            isPercent: data.percent,
            period: discountPeriodToFrequency(data.period),
            title: data.title || i18next.t('discounts.discount'),
            isSurcharge: data.title === 'Surcharge',
            discountId: generateUUID(),
            showOnInvoice: data.showOnInvoice,
            ordering: data.ordering,
        });
    }

    if (data.__typename === 'PlanPresetDiscount') {
        return new Discount({
            amount: data.discountPreset.amount,
            title: data.discountPreset.title || i18next.t('discounts.discount'),
            discountId: generateUUID(),
            discountPresetId: data.discountPreset.discountPresetId,
            ordering: data.discountPreset.ordering,
            isSurcharge: data.discountPreset.title === 'Surcharge',
        });
    }

    if (data.__typename === 'PlanSurcharge') {
        return new Discount({
            amount: data.amount,
            isPercent: data.percent,
            title: data.title || i18next.t('discounts.surcharge'),
            discountId: generateUUID(),
            period: discountPeriodToFrequency(data.period),
            showOnInvoice: data.showOnInvoice,
            isSurcharge: data.title === 'Surcharge',
            ordering: data.ordering,
        });
    }

    return null;
};

export class PublicFundingSettings extends Record<{
    readonly method: Constants.PUBLIC_FUNDING_TYPE;
    readonly amount: number;
    readonly hours: number | null;
    readonly minutes: number | null;
    readonly rate: number | null;
    readonly fundingTypeIds: List<string>;
    readonly period: Constants.FREQUENCY;
    readonly maxFundedMinutes: number | null;
}>({
    method: 'NONE',
    amount: 0,
    hours: null,
    minutes: null,
    rate: null,
    fundingTypeIds: List<string>(),
    period: 'WEEKLY',
    maxFundedMinutes: null,
}) {}

export const publicFundingSettingsFromApi = (data: any) => {
    if (data.method === 'ASSISTED') {
        return new PublicFundingSettings({
            ...data,
            fundingTypeIds: List(data.fundingTypeIds),
        });
    } else if (data.method === 'MANUAL') {
        return new PublicFundingSettings({
            ...data,
            fundingTypeIds: List(data.fundingTypeIds),
        });
    } else if (data.method === 'MANUAL_BY_RATE') {
        return new PublicFundingSettings({
            ...data,
            fundingTypeIds: List(data.fundingTypeIds),
        });
    }

    return new PublicFundingSettings({
        method: 'UNKNOWN',
    });
};

const publicFundingTypenameToMethod = (
    typename?: 'PlanManualByRatePublicFunding' | 'PlanManualPublicFunding' | 'PlanAssistedPublicFunding',
) => {
    switch (typename) {
        case 'PlanManualByRatePublicFunding':
            return 'MANUAL_BY_RATE';
        case 'PlanManualPublicFunding':
            return 'MANUAL';
        case 'PlanAssistedPublicFunding':
            return 'ASSISTED';
        default:
            return 'UNKNOWN';
    }
};

const publicFundingFrequencyToPeriod = (frequency: FinancePeriodFunding) => {
    switch (frequency) {
        case FinancePeriodFunding.WEEKLY:
            return 'WEEKLY';
        case FinancePeriodFunding.MONTHLY:
            return 'MONTHLY';
        case FinancePeriodFunding.YEARLY:
            return 'YEARLY';
        default:
            return 'WEEKLY';
    }
};

export const publicFundingSettingsFromGraphQL = (data?: Maybe<PlanPublicFundingSettings>) => {
    if (!data) {
        return null;
    }

    const {__typename, period, time, ...narrowedData} = data;
    return new PublicFundingSettings({
        method: publicFundingTypenameToMethod(data.__typename),
        ...(narrowedData as any),
        hours: data.time.__typename === 'PlanFundingTimeHours' ? data.time.hours : null,
        minutes: data.time.__typename === 'PlanFundingTimeMinutes' ? data.time.minutes : null,
        fundingTypeIds: List(data.fundingTypeIds),
        period: publicFundingFrequencyToPeriod(data.period),
    });
};

// Resembles a Billing/Invoicing profile
// The `id` field is the billing scheme.
// It would be nice to rename this to
// BillingProfile (also in the API)
export class Billing extends Record<{
    readonly id: string;
    readonly invoices: number;
    readonly weeksOfCare: number;
    readonly title: string;
}>({
    id: '',
    invoices: 12,
    weeksOfCare: 52,
    title: '',
}) {}

const billingFromAPI = (data: any) => {
    return new Billing({
        ...data,
    });
};

const billingFromGraphQL = (planPart: GqlPlanPart) =>
    new Billing({
        id: planPart.billingScheme,
        invoices: planPart.invoices,
        weeksOfCare: planPart.weeksOfCare,
        title: planPart.billingScheme,
    });

interface IPlanPart {
    readonly ordering: number;
    readonly billingProfileId: string | null;
    readonly planPartId: string | null;
    readonly attendanceScheduleId: string | null;
    readonly termTimeOnly?: boolean;
    readonly billing: Billing;
    readonly discounts: List<Discount>;
    readonly sessionBookings: List<SessionBooking>;
    readonly productBookings: List<ProductBooking>;
    readonly chargeRuleExemptions: string[];
}

export class PlanPart extends Record<IPlanPart>({
    ordering: 0,
    planPartId: null,
    billingProfileId: null,
    attendanceScheduleId: null,
    termTimeOnly: undefined,
    billing: new Billing(),
    discounts: List<Discount>(),
    sessionBookings: List<SessionBooking>(),
    productBookings: List<ProductBooking>(),
    chargeRuleExemptions: [],
}) {}

const planPartFromAPI = (data: any) => {
    return new PlanPart({
        ...data,
        billing: billingFromAPI(data.billing),
        sessionBookings: List(data.sessionBookings).map(SessionBookingEntity.fromAPI),
        productBookings: List(data.productBookings).map(ProductBookingEntity.fromAPI),
        discounts: List(data.discounts).map(discountFromAPI),
    });
};

const planPartFromGraphQL = (planPart: GqlPlanPart) => {
    return new PlanPart({
        ...planPart,
        billing: billingFromGraphQL(planPart),
        sessionBookings: List(planPart.sessionBookings).map(SessionBookingEntity.fromGraphQL),
        productBookings: List(planPart.productBookings).map(ProductBookingEntity.fromGraphQL),
        discounts: List(planPart.priceModifiers).map(discountFromGraphQL).filter(Boolean) as List<Discount>,
    });
};

export type IPlan = {
    readonly id: string | null;
    readonly pricingGroupId: string | null;
    readonly ruleGroupId: string | null;
    readonly note: string | null;
    readonly from: Moment | null;
    readonly to: Moment | null;
    readonly behaviors: BehaviorsCollection;
    readonly publicFundingSettings: PublicFundingSettings | null;
    readonly planStates: List<PlanState>;
    readonly planParts: List<PlanPart>;
    readonly childId: string | null;
};

const PlanFactory = Record<IPlan>({
    id: null,
    pricingGroupId: null,
    ruleGroupId: null,
    note: '',
    from: null,
    to: null,
    behaviors: new BehaviorsCollection([]),
    publicFundingSettings: null,
    planStates: List(),
    planParts: List(),
    childId: null,
});

export type Plan = InstanceType<typeof PlanFactory>;

const fromAPI = (data: any) => {
    return new PlanFactory({
        ...data,
        to: data.to ? moment(data.to, 'YYYY-MM-DD') : null,
        from: data.from ? moment(data.from, 'YYYY-MM-DD') : null,
        behaviors: new BehaviorsCollection(data.behaviors),
        publicFundingSettings: data.publicFundingSettings
            ? publicFundingSettingsFromApi(data.publicFundingSettings)
            : null,
        planStates: List(data.planStates).map(PlanStateEntity.fromAPI),
        planParts: List(data.planParts)
            .map(planPartFromAPI)
            .sortBy(part => part.ordering),
    });
};

export const fromGraphQL = (data: PricedPlan) =>
    new PlanFactory({
        id: data.planId,
        childId: data.childId,
        pricingGroupId: data.pricing.__typename === 'PricingGroupPricing' ? data.pricing.pricingGroupId : null,
        ruleGroupId: data.pricing.__typename === 'AutomaticPricing' ? data.pricing.ruleGroupId : null,
        note: data.note,
        from: data.range.from ? moment(data.range.from, 'YYYY-MM-DD') : null,
        to: data.range.to ? moment(data.range.to, 'YYYY-MM-DD') : null,
        behaviors: new BehaviorsCollection(
            data.behaviors.map(behavior => ({
                ...behavior,
                payload: behavior.payload ? JSON.parse(behavior.payload) : null,
            })),
        ),
        publicFundingSettings: data.planParts[0].publicFundingSettings
            ? publicFundingSettingsFromGraphQL(data.planParts[0].publicFundingSettings)
            : null,
        planStates: List(data.planStates).map(PlanStateEntity.fromGraphQL),
        planParts: List(data.planParts)
            .map(planPartFromGraphQL)
            .sortBy(part => part.ordering),
    });

const publicFundingSettingsToAPI = (publicFundingSettings: PublicFundingSettings) => {
    if (publicFundingSettings.method === 'ASSISTED') {
        const isMinutes = publicFundingSettings.minutes !== null;
        return {
            method: publicFundingSettings.method,
            hours: isMinutes ? null : publicFundingSettings.hours,
            minutes: isMinutes ? publicFundingSettings.minutes : null,
            fundingTypeIds: publicFundingSettings.fundingTypeIds.toJS(),
            maxFundedMinutes: publicFundingSettings.maxFundedMinutes,
        };
    } else if (publicFundingSettings.method === 'MANUAL') {
        const isMinutes = publicFundingSettings.minutes !== null;
        return {
            method: publicFundingSettings.method,
            hours: isMinutes ? null : publicFundingSettings.hours,
            minutes: isMinutes ? publicFundingSettings.minutes : null,
            rate: publicFundingSettings.rate,
            amount: publicFundingSettings.amount,
            period: publicFundingSettings.period,
            fundingTypeIds: publicFundingSettings.fundingTypeIds.toJS(),
            maxFundedMinutes: publicFundingSettings.maxFundedMinutes,
        };
    } else if (publicFundingSettings.method === 'MANUAL_BY_RATE') {
        const isMinutes = publicFundingSettings.minutes !== null;
        return {
            method: publicFundingSettings.method,
            hours: isMinutes ? null : publicFundingSettings.hours,
            minutes: isMinutes ? publicFundingSettings.minutes : null,
            rate: publicFundingSettings.rate,
            period: publicFundingSettings.period,
            fundingTypeIds: publicFundingSettings.fundingTypeIds.toJS(),
            maxFundedMinutes: publicFundingSettings.maxFundedMinutes,
        };
    }

    return null;
};

const momentToApi = (date?: Moment | null) => {
    return date ? date.format('YYYY-MM-DD') : null;
};

const planPartToAPI = (part: PlanPart, ordering: number): PlanPartApi => {
    return {
        ordering,
        planPartId: part.planPartId || '',
        billingProfileId: part.billingProfileId || '',
        sessionBookings: part.sessionBookings.map(booking => ({
            sessionId: booking.sessionId,
            fundable: booking.fundable,
            day: booking.day,
            start: booking.start ? booking.start.format('HH:mm') : null,
            end: booking.end ? booking.end.format('HH:mm') : null,
            planRuleId: booking.planRuleId,
        })),
        chargeRuleExemptions: part.chargeRuleExemptions || [],
        productBookings: part.productBookings.map(booking => ({
            productId: booking.productId,
            amount: booking.amount,
            day: booking.day,
            bookedPrice: booking.bookedPrice,
            planRuleId: booking.planRuleId,
            bookingStatus: booking.bookingStatus,
            isVirtual: booking.isVirtual,
        })),
        billing: part.billing ? part.billing.toJS() : null,
        termTimeOnly: part.termTimeOnly,
        attendanceScheduleId: part.attendanceScheduleId || '',
        termScheduleId: null,
        discounts: part.discounts
            .map((discount, i) => ({
                amount: discount.amount,
                isPercent: discount.isPercent,
                period: discount.period,
                title: discount.title,
                showOnInvoice: discount.showOnInvoice,
                origin: discount.origin,
                discountPresetId: discount.discountPresetId ? discount.discountPresetId : undefined,
                ordering: i,
            }))
            .toJS(),
    };
};

const toAPI = (plan: Plan): CreateUpdatePlanPayload => {
    const planFrom = momentToApi(plan.from);
    const to = momentToApi(plan.to);
    const returnPlan: CreateUpdatePlanPayload = {
        id: plan.id || '',
        childId: plan.childId || '',
        pricingGroupId: plan.pricingGroupId === '' ? null : plan.pricingGroupId,
        ruleGroupId: plan.ruleGroupId ?? null,
        note: plan.note || '',
        from: planFrom,
        to,
        planParts: plan.planParts.map(planPartToAPI).toArray(),
    };

    if (plan.publicFundingSettings) {
        returnPlan.publicFundingSettings = publicFundingSettingsToAPI(plan.publicFundingSettings);
    }

    return returnPlan;
};

const PlanEntityModel: EntityModel<IPlan> = {
    record: PlanFactory,
    default: new PlanFactory({
        planParts: List([
            new PlanPart({
                planPartId: generateUUID(),
            }),
        ]),
    }),
    fromAPI,
    toAPI,
    records: {
        Billing,
        PublicFundingSettings,
        PlanPart,
        Discount,
    },
};

export default PlanEntityModel;
